import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { api } from '../../lib/api'
import { KpiCard, Section } from '../../components/ui'
import { StatusBanner } from '../../components/StatusBanner'
import { Timeline } from '../../components/Timeline'
import { NotesPanel } from '../../components/NotesPanel'
import { RelatedEntityList } from '../../components/RelatedEntityList'
import type {
  Organization, OrganizationSummary, OrganizationHealth, BillingEvent, Patient, Practice, Provider, AdminContinuity,
} from '../../lib/types'

const BILLING_STATUSES = ['trial', 'manual', 'active', 'paused', 'cancelled']
const PLAN_OPTIONS = [
  { value: '', label: '—' },
  { value: 'solo_provider', label: 'Solo Provider' },
  { value: 'group_practice', label: 'Group Practice' },
  { value: 'clinic', label: 'Clinic' },
  { value: 'enterprise', label: 'Enterprise' },
]

export default function AdminOrgDetail() {
  const { orgId } = useParams<{ orgId: string }>()
  const [org, setOrg] = useState<Organization | null>(null)
  const [health, setHealth] = useState<OrganizationHealth | null>(null)
  const [summary, setSummary] = useState<OrganizationSummary | null>(null)
  const [billingEvents, setBillingEvents] = useState<BillingEvent[]>([])
  const [providers, setProviders] = useState<Provider[]>([])
  const [patients, setPatients] = useState<Patient[]>([])
  const [practices, setPractices] = useState<Practice[]>([])
  const [continuity, setContinuity] = useState<AdminContinuity | null>(null)
  const [billingNotes, setBillingNotes] = useState('')
  const [stripeCustomerId, setStripeCustomerId] = useState('')
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState('')
  const [saving, setSaving] = useState(false)

  async function load() {
    const [o, h, s, be, provs, pts, pracs, cont] = await Promise.all([
      api(`/organizations/${orgId}`),
      api(`/admin/organizations/${orgId}/health`),
      api(`/admin/organizations/${orgId}/summary`),
      api(`/admin/organizations/${orgId}/billing-events`),
      api(`/providers?organization_id=${orgId}`),
      api(`/patients?organization_id=${orgId}`),
      api(`/practices?organization_id=${orgId}`),
      api(`/admin/continuity?organization_id=${orgId}`),
    ])
    setOrg(o)
    setHealth(h)
    setSummary(s)
    setBillingEvents(be)
    setBillingNotes(o.billing_notes || '')
    setStripeCustomerId(o.stripe_customer_id || '')
    setStripeSubscriptionId(o.stripe_subscription_id || '')
    setProviders(provs)
    setPatients(pts)
    setPractices(pracs)
    setContinuity(cont)
  }

  useEffect(() => { load() }, [orgId])

  async function patch(fields: Record<string, unknown>) {
    setSaving(true)
    const updated = await api(`/organizations/${orgId}`, { method: 'PATCH', body: JSON.stringify(fields) })
    setOrg(updated)
    setSaving(false)
  }

  if (!org || !orgId) return <div className="page-loading">Loading organization…</div>

  const suspended = org.status === 'suspended' || org.status === 'inactive'

  return (
    <div>
      <Link to="/admin/organizations" className="muted" style={{ fontSize: 13 }}>&larr; All organizations</Link>
      <div className="page-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
        <div>
          <h1>{org.name}</h1>
          <div className="ph-sub">{org.type} · created {new Date(org.created_at).toLocaleDateString()}</div>
        </div>
        <button
          className={`btn small ${!suspended ? 'outline' : ''}`}
          disabled={saving}
          onClick={() => patch({ status: suspended ? 'active' : 'suspended' })}
        >
          {suspended ? 'Reactivate organization' : 'Suspend organization'}
        </button>
      </div>

      {/* --- Health --- */}
      {health && (
        <Section title="Health">
          <StatusBanner
            riskLevel={health.risk_level}
            score={health.health_score}
            components={[
              { label: 'Session 4 rate', value: health.components.session_4_rate != null ? `${Math.round(health.components.session_4_rate * 100)}%` : '—' },
              { label: 'Early dropout rate', value: health.components.early_dropout_rate != null ? `${Math.round(health.components.early_dropout_rate * 100)}%` : '—' },
              { label: 'Active patients', value: health.components.active_patients },
              { label: 'Active providers', value: health.components.active_providers },
              { label: 'Continuity alerts open', value: health.components.continuity_alerts_open },
              { label: 'Billing status', value: health.components.billing_status },
              { label: 'Last activity', value: health.components.last_activity_at ? new Date(health.components.last_activity_at).toLocaleDateString() : 'No activity yet' },
            ]}
          />
        </Section>
      )}

      {summary && (
        <div className="section-block">
          <div className="section-head"><h2>Operations summary</h2></div>
          <div className="kpis">
            <KpiCard label="Active providers" value={summary.active_providers} />
            <KpiCard label="Credentialed providers" value={summary.credentialed_providers} />
            <KpiCard label="Active patients" value={summary.active_patients} />
            <KpiCard label="Session 4 completions" value={summary.session_4_completions} />
            <KpiCard label="Active billable seats" value={summary.active_billable_seats} />
            <KpiCard label="Estimated outcome fees" value={`$${(summary.estimated_outcome_fees_cents / 100).toFixed(2)}`} />
          </div>
        </div>
      )}

      {/* --- Timeline --- */}
      <Section title="Timeline">
        <Timeline organizationId={orgId} limit={25} />
        <Link to="/admin/event-logs" className="muted" style={{ fontSize: 13 }}>View full event log &rarr;</Link>
      </Section>

      {/* --- Tasks & Notes --- */}
      <NotesPanel attachedToType="organization" attachedToId={orgId} organizationId={orgId} />

      {/* --- Related entities --- */}
      <Section title="Related providers" note={`${providers.length}`}>
        <RelatedEntityList
          items={providers.map((p) => ({
            id: p.id, to: `/admin/providers/${p.id}`,
            primary: p.name,
            secondary: `${p.capacity_status} · ${p.credentialing_status}`,
          }))}
          emptyLabel="No providers yet."
        />
      </Section>

      <Section title="Related patients" note={`${patients.length}`}>
        <RelatedEntityList
          items={patients.map((p) => ({
            id: p.id, to: `/admin/patients/${p.id}`,
            primary: p.name,
            secondary: p.status,
          }))}
          emptyLabel="No patients yet."
        />
      </Section>

      <div className="section-block">
        <div className="section-head"><h2>Practices</h2><span className="sh-note">{practices.length}</span></div>
        <div className="row-list">
          {practices.map((p) => <div key={p.id} className="row"><span>{p.name}{p.location ? ` · ${p.location}` : ''}</span><span className="muted">{p.status}</span></div>)}
          {practices.length === 0 && <div className="empty">No practices yet.</div>}
        </div>
      </div>

      {/* --- Billing --- */}
      <div className="section-block">
        <div className="section-head"><h2>Activation &amp; plan</h2></div>
        <div className="card form-grid">
          <div className="field">
            <label>Status</label>
            <select className="inp" value={suspended ? 'suspended' : 'active'} onChange={(e) => patch({ status: e.target.value })}>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div className="field">
            <label>Selected plan</label>
            <select className="inp" value={org.selected_plan || ''} onChange={(e) => patch({ selected_plan: e.target.value })}>
              {PLAN_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Billing status</label>
            <select className="inp" value={org.billing_status} onChange={(e) => patch({ billing_status: e.target.value })}>
              {BILLING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="field" style={{ marginTop: 10 }}>
          <label>Billing notes</label>
          <textarea className="inp" rows={2} value={billingNotes} onChange={(e) => setBillingNotes(e.target.value)} onBlur={() => patch({ billing_notes: billingNotes })} />
        </div>
        {saving && <div className="muted" style={{ fontSize: 12.5 }}>Saving…</div>}
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Billing settings</h2></div>
        <div className="card">
          <div className="row" style={{ marginBottom: 10 }}>
            <span>Stripe connected</span>
            <span className={`badge ${org.stripe_connected ? 'badge-green' : 'badge-grey'}`}>{org.stripe_connected ? 'Yes' : 'No'}</span>
          </div>
          <div className="form-grid">
            <div className="field">
              <label>Stripe customer ID</label>
              <input className="inp" value={stripeCustomerId} onChange={(e) => setStripeCustomerId(e.target.value)} onBlur={() => patch({ stripe_customer_id: stripeCustomerId || null })} />
            </div>
            <div className="field">
              <label>Stripe subscription ID</label>
              <input className="inp" value={stripeSubscriptionId} onChange={(e) => setStripeSubscriptionId(e.target.value)} onBlur={() => patch({ stripe_subscription_id: stripeSubscriptionId || null })} />
            </div>
          </div>
          <p className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
            Stripe is not configured. These fields are placeholders for manual entry until live Stripe integration is built.
          </p>
        </div>
      </div>

      <div className="section-block">
        <div className="section-head"><h2>Billing ledger</h2><span className="sh-note">{billingEvents.length}</span></div>
        <div className="row-list">
          {billingEvents.map((b) => (
            <div key={b.id} className="row">
              <span>{b.event_type.replace('_', ' ')}</span>
              <span className="muted">${(b.amount_cents / 100).toFixed(2)} · {new Date(b.created_at).toLocaleDateString()}</span>
            </div>
          ))}
          {billingEvents.length === 0 && <div className="empty">No billing events recorded yet.</div>}
        </div>
      </div>

      {/* --- Credentialing --- */}
      <Section title="Credentialing summary">
        <div className="kpis">
          {['unverified', 'submitted', 'in_review', 'approved', 'rejected'].map((status) => (
            <KpiCard key={status} label={status.replace('_', ' ')} value={providers.filter((p) => p.credentialing_status === status).length} />
          ))}
        </div>
      </Section>

      {continuity && (
        <Section title="Continuity metrics">
          <div className="kpis">
            <KpiCard label="Matched, no Session 1" value={continuity.counts.matched_no_session_1} />
            <KpiCard label="Session 1 attended" value={continuity.counts.session_1_attended} />
            <KpiCard label="Approaching Session 4" value={continuity.counts.approaching_session_4} />
            <KpiCard label="Session 4 reached" value={continuity.counts.session_4_reached} />
            <KpiCard label="Stalled" value={continuity.counts.stalled} />
            <KpiCard label="Needing check-in" value={continuity.counts.needs_checkin} />
          </div>
        </Section>
      )}
    </div>
  )
}
